import { createContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import UserService from '@src/services/UserService';
import { useTimeTracker } from '../../contexts/TimeTrackerProvider';
import {
  isAllUsersSelected,
  parseUsersSelectedFromUrl,
} from '../../utils/usersSelectedParams';

const defaultValue = {
  isDrawerOpen: false,
  setIsDrawerOpen: () => {},
  selectedReportRecord: null,
  setSelectedReportRecord: () => {},
  selectedAttendanceRecord: null,
  setSelectedAttendanceRecord: () => {},
  selectedIndividualUser: null,
  setSelectedIndividualUser: () => {},
  usersToSelect: [],
  setUsersToSelect: () => {},
  getSelectedIndividualUser: () => null,
  usersSelected: [],
  setUsersSelected: () => {},
  isAllUsersSelected: false,
  individualUserId: '',
};

export const ReportContext = createContext(defaultValue);

export const ReportProvider = ({ children }) => {
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedReportRecord, setSelectedReportRecord] = useState(null);
  const [selectedAttendanceRecord, setSelectedAttendanceRecord] = useState(null);
  const [selectedIndividualUser, setSelectedIndividualUser] = useState(null);
  const [usersToSelect, setUsersToSelect] = useState([]);

  const [usersSelected, setUsersSelected] = useState([]); //TODO: this will replace selectedIndividualUser
  const { organizationIds } = useTimeTracker();

  const userService = new UserService();

  const allUsersSelected = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return isAllUsersSelected(queryParams.get('usersSelected'));
  }, [location.search]);

  // userId is only in the URL of individual reports; selectedIndividualUser can outlive it.
  const individualUserId = useMemo(
    () => new URLSearchParams(location.search).get('userId') || '',
    [location.search],
  );

  const userIdsParams = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    const individualUserId = queryParams.get('userId') || '';
    const groupUserIds = queryParams.get('usersSelected') || '';

    if (isAllUsersSelected(groupUserIds)) {
      return individualUserId ? [individualUserId] : [];
    }

    return [individualUserId, ...parseUsersSelectedFromUrl(groupUserIds)].filter(Boolean);
  }, [location.search]);

  const getSelectedIndividualUser = userId => {
    const user = usersToSelect.find(user => user._id === userId);
    if (!user) return null;
    return {
      id: user._id,
      ...user._source,
    };
  };

  // Fetch the selected users from the query params ,
  // when refreshing the page, the userId is not in the usersToSelect array,
  // so we need to find it in the query params
  useEffect(() => {
    const fetchSelectedIndividualUser = async () => {
      const queryParams = new URLSearchParams(location.search);
      const individualUserId = queryParams.get('userId') || '';

      const users = await userService.listElastic(
        {
          orgId: organizationIds[0],
          divsIds: [],
          pageSize: 100,
          from: 0,
          ids: userIdsParams,
        },
        false,
      );
      const hits = users?.hits?.hits || [];
      const usersData = hits.map(hit => hit._source);
      if (usersData.length === 0) return;

      setUsersSelected(usersData);

      if (individualUserId) {
        const individualHit = hits.find(hit => hit._id === individualUserId);
        if (individualHit) {
          setSelectedIndividualUser({ id: individualHit._id, ...individualHit._source });
        }
      }
    };
    if (userIdsParams.length > 0 && organizationIds?.length > 0) {
      fetchSelectedIndividualUser();
    } else if (allUsersSelected) {
      setUsersSelected([]);
    }
  }, [userIdsParams.length, organizationIds?.length, location.search, allUsersSelected]);

  const value = {
    isDrawerOpen,
    setIsDrawerOpen,
    selectedReportRecord,
    setSelectedReportRecord,
    selectedAttendanceRecord,
    setSelectedAttendanceRecord,
    selectedIndividualUser,
    setSelectedIndividualUser,
    usersToSelect,
    setUsersToSelect,
    getSelectedIndividualUser,
    usersSelected,
    isAllUsersSelected: allUsersSelected,
    individualUserId,
  };

  return <ReportContext.Provider value={value}>{children}</ReportContext.Provider>;
};
